import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Battery, Sun, Zap, TrendingUp, Activity, Leaf, ArrowDownToLine, ArrowUpFromLine, Fuel, DollarSign, RefreshCw, Database } from 'lucide-react';
import { formatNumber } from '@/lib/utils';
import { getDemoSoc, getDemoGeneration, getDemoConsumption } from '@/lib/demoData';
import { currentSupabaseEnv } from '@/lib/supabase';
import { useState, useMemo } from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import type { SolarSystem } from '@/types';
import { liveBatteryView } from '@/lib/legacyStatusIcons';
import { BatteryCriticalTag } from '@/components/unit/BatteryCriticalTag';
import type { UnitLiveStatus } from '@/lib/unitLiveStatus';
import { useEffectiveRate } from '@/hooks/useEffectiveRate';
import { useRBAC } from '@/contexts/RBACContext';
import { authenticatedFetch } from '@/lib/api';
import { toast } from 'react-toastify';

// Energy calculation constants (CO2 and diesel remain static)
const ENERGY_CONSTANTS = {
  DIESEL_KWH_PER_GALLON: 38.6,
  CO2_KG_PER_DIESEL_GALLON: 10.18,
};

interface SolarPanelProps {
  system: SolarSystem;
  unitId?: string;
  /** T-197: the server's status icons; the battery number, colour and bar come from its band. */
  liveStatus?: UnitLiveStatus | null;
}

export function SolarPanel({ system, unitId, liveStatus }: SolarPanelProps) {
  // Fetch effective rate for this unit (with fallback if API fails)
  const { rate } = useEffectiveRate({ unitId, autoFetch: !!unitId });
  const { isAdmin, isSuperadmin } = useRBAC();
  const [isBackfilling, setIsBackfilling] = useState(false);

  // Check if user has admin access for backfill functionality
  const canBackfill = isAdmin || isSuperadmin;

  // Trigger historical data backfill for this unit
  const handleBackfill = async () => {
    if (!unitId || isBackfilling) return;

    setIsBackfilling(true);
    try {
      const response = await authenticatedFetch(`/api/v1/admin/units/${unitId}/backfill`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate: '2024-01-01',
          endDate: new Date().toISOString().split('T')[0],
        }),
      });

      const data = await response.json() as {
        success?: boolean;
        skipped?: boolean;
        reason?: string;
        records?: { upserted?: number };
        records_upserted?: number;
        error?: string;
        message?: string;
      };

      if (!response.ok) {
        const errorMsg = data.message || data.error || 'Backfill failed';
        if (errorMsg.includes('production')) {
          toast.info('Sol-Ark backfill is only available in production environment');
        } else if (errorMsg.includes('inverter')) {
          toast.warning('No Sol-Ark inverter linked to this unit');
        } else {
          toast.error(errorMsg);
        }
        return;
      }

      if (data.skipped) {
        toast.info(`Backfill skipped: ${data.reason}`);
      } else {
        const recordCount = data.records_upserted || data.records?.upserted || 0;
        toast.success(`Backfill complete! ${recordCount} records synced`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Backfill error:', error);
      toast.error(errorMessage || 'Failed to trigger backfill');
    } finally {
      setIsBackfilling(false);
    }
  };

  // Use effective rates or fallback values
  const gridImportRate = rate?.gridImportRate ?? 0.18;
  const gridSellRate = rate?.gridSellRate ?? 0.08;

  // Use demo data when in demo mode and real values are 0 (missing data)
  const inDemoMode = currentSupabaseEnv === 'demo';
  const batteryLevel = inDemoMode && system.battery === 0 && unitId
    ? getDemoSoc(unitId)
    : system.battery;
  const generation = inDemoMode && system.generation === 0 && unitId
    ? getDemoGeneration(unitId)
    : system.generation;
  const consumption = inDemoMode && system.consumption === 0 && unitId
    ? getDemoConsumption(unitId)
    : system.consumption;

  // Debug logging for battery SoC data flow
  if (process.env.NODE_ENV === 'development') {
    console.log('[SolarPanel] Battery SoC data:', {
      unitId,
      currentSupabaseEnv,
      inDemoMode,
      rawSystemBattery: system.battery,
      calculatedBatteryLevel: batteryLevel,
      usingDemoData: inDemoMode && system.battery === 0,
      rawGeneration: system.generation,
      rawConsumption: system.consumption,
    });
  }

  // T-197: App's battery lines (50/30) from the server's band, like the map. This panel
  // never works out a band itself; without live_status the number shows uncoloured.
  const battery = liveBatteryView(liveStatus, batteryLevel);
  const batteryText = battery.text ?? 'text-muted-foreground';

  // Generate simple historical data if not provided
  // Validate that history has valid numeric generation values to prevent ApexCharts parser errors
  const hasValidHistory = system.history &&
    Array.isArray(system.history) &&
    system.history.length > 1 &&
    system.history.every(h => h && typeof h.generation === 'number' && Number.isFinite(h.generation));

  // No history means no chart — it does NOT mean invent one.
  //
  // This used to fall back to 24 hours of Math.random() generation/consumption/
  // battery values, rendered identically to measured data. A unit whose Sol-Ark
  // had never reported showed a plausible solar curve. Real data or nothing.
  const history = useMemo(
    () => (hasValidHistory ? system.history! : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasValidHistory, unitId],
  );

  // Memoize chart data arrays to keep them stable across re-renders
  const { timeLabels, generationData } = useMemo(() => {
    const labels = history.map(h =>
      new Date(h.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    );
    // Sanitize generation data to prevent ApexCharts parser errors from NaN/null/undefined values
    const data = history.map(h => {
      const val = Number(h.generation);
      return Number.isFinite(val) ? val : 0;
    });
    return { timeLabels: labels, generationData: data };
  }, [history]);

  // Memoize ApexCharts options and series to prevent re-creating on every render.
  // Unstable references cause ApexCharts to call updateOptions/updateSeries internally,
  // which triggers SVG path re-parsing and animation that can fail with "parser Error".
  const generationChartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: 'area' as const,
      background: 'transparent',
      toolbar: { show: false },
      zoom: { enabled: false },
      sparkline: { enabled: false },
      animations: { dynamicAnimation: { enabled: false } },
    },
    stroke: { curve: 'smooth', width: 2 },
    colors: ['#8FB560'], // Hollywood Trucks green (success)
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 100] },
    },
    theme: { mode: 'dark' },
    grid: { borderColor: '#374151', strokeDashArray: 3 },
    xaxis: {
      categories: timeLabels,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: '#9CA3AF', fontSize: '10px' },
        formatter: (val: number) => `${formatNumber(val, 1)}`,
      },
    },
    tooltip: {
      theme: 'dark',
      y: { formatter: (val: number) => `${formatNumber(val, 2)} kW` },
    },
    dataLabels: { enabled: false },
  }), [timeLabels]);

  const generationChartSeries = useMemo(
    () => [{ name: 'Generation', data: generationData }],
    [generationData]
  );
  return (
    <Card className="lg:min-h-[647px] flex flex-col mb-4 lg:mb-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sun className="h-5 w-5 text-success" />
            <CardTitle>Sol-Ark Solar System</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {canBackfill && unitId && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBackfill}
                disabled={isBackfilling}
                className="text-xs"
                title="Sync historical solar data from Sol-Ark"
              >
                {isBackfilling ? (
                  <>
                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Database className="h-3 w-3 mr-1" />
                    Backfill Data
                  </>
                )}
              </Button>
            )}
            <Badge variant={system.enabled ? 'success' : 'secondary'}>
              {system.enabled ? 'Active' : 'Offline'}
            </Badge>
          </div>
        </div>
        <CardDescription>Solar power generation and battery storage</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 flex-1">
        {/* Compact Power Overview - Single Row */}
        <div className="grid grid-cols-4 gap-3">
          {/* Battery */}
          <div className="text-center">
            <Battery className={`h-5 w-5 mx-auto mb-1 ${batteryText}`} />
            <div className={`text-xl font-bold ${batteryText}`}>
              {battery.level == null ? '—' : `${battery.level}%`}
            </div>
            <div className="text-[10px] text-muted-foreground">Battery</div>
            <BatteryCriticalTag show={battery.critical} className="mt-0.5" />
            {system.chargeRate !== undefined && system.chargeRate !== 0 && (
              <div className="text-[10px] text-muted-foreground">
                {system.status === 'charging' ? '+' : '-'}{formatNumber(Math.abs(system.chargeRate), 1)}kW
              </div>
            )}
          </div>
          {/* Generation */}
          <div className="text-center">
            <Sun className="h-5 w-5 mx-auto mb-1 text-success" />
            <div className="text-xl font-bold text-success">
              {formatNumber(generation, 1)}
            </div>
            <div className="text-[10px] text-muted-foreground">Gen kW</div>
          </div>
          {/* Consumption */}
          <div className="text-center">
            <Zap className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
            <div className="text-xl font-bold">{formatNumber(consumption, 1)}</div>
            <div className="text-[10px] text-muted-foreground">Load kW</div>
          </div>
          {/* Net Power */}
          <div className="text-center">
            <Activity className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
            <div className={`text-xl font-bold ${
              generation - consumption >= 0 ? 'text-success' : 'text-error'
            }`}>
              {generation - consumption >= 0 ? '+' : ''}{formatNumber(generation - consumption, 1)}
            </div>
            <div className="text-[10px] text-muted-foreground">Net kW</div>
          </div>
        </div>

        {/* Battery Bar - Compact */}
        <div className="relative h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`absolute inset-y-0 left-0 rounded-full transition-all ${battery.fill ?? 'bg-muted-foreground/40'}`}
            style={{ width: `${battery.level ?? 0}%` }}
          />
        </div>

        {/* Secondary Stats Row */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="capitalize">{system.status}</span>
          {/* Physical inverters in the plant — absent until the first full sync;
              single-inverter plants (the norm) render nothing here. */}
          {system.physicalInverterCount !== undefined && system.physicalInverterCount > 1 && (
            <span>Inverters: {system.physicalInverterCount}</span>
          )}
          {system.todayGeneration !== undefined && (
            <span>Today: {formatNumber(system.todayGeneration, 1)} kWh</span>
          )}
        </div>

        {/* 24-Hour Generation Chart */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">24-Hour Generation</span>
          </div>
          <div className="relative h-[120px] bg-muted/30 rounded-lg">
            {hasValidHistory ? (
              <>
                <Chart
                  type="area"
                  options={generationChartOptions}
                  series={generationChartSeries}
                  height={120}
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground px-3 -mt-2">
                  <span>24h ago</span>
                  <span>Now</span>
                </div>
              </>
            ) : (
              <div className="flex h-full items-center justify-center">
                <span className="text-xs text-muted-foreground">No generation history recorded</span>
              </div>
            )}
          </div>
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border">
          <div className="text-center">
            <Activity className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
            <div className="text-lg font-bold">
              {formatNumber((system.powerOut || system.generation) / (system.powerIn || system.consumption || 1) * 100, 0)}%
            </div>
            <div className="text-xs text-muted-foreground">Efficiency</div>
          </div>
          <div className="text-center">
            <Zap className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
            <div className="text-lg font-bold">
              {formatNumber(system.todayGeneration || system.generation * 8, 1)}
            </div>
            <div className="text-xs text-muted-foreground">kWh Today</div>
          </div>
        </div>

        {/* Cumulative Energy Metrics - Only show when data is available */}
        {(system.loadKwh !== undefined || system.batteryChargeKwh !== undefined) && (
          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Cumulative Energy (7 Day)</span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Load */}
              <div className="bg-muted/30 rounded-lg p-3 text-center">
                <Zap className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                <div className="text-lg font-bold">{formatNumber(system.loadKwh || 0, 1)}</div>
                <div className="text-xs text-muted-foreground">Load kWh</div>
              </div>
              {/* PV */}
              <div className="bg-muted/30 rounded-lg p-3 text-center">
                <Sun className="h-4 w-4 mx-auto mb-1 text-success" />
                <div className="text-lg font-bold text-success">
                  {formatNumber((system.dailyGeneration ?? system.todayGeneration ?? 0) * 7, 1)}
                </div>
                <div className="text-xs text-muted-foreground">PV kWh</div>
              </div>
              {/* Grid Import */}
              <div className="bg-muted/30 rounded-lg p-3 text-center">
                <ArrowDownToLine className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                <div className="text-lg font-bold">{formatNumber(system.gridImportKwh || 0, 1)}</div>
                <div className="text-xs text-muted-foreground">Grid In</div>
              </div>
              {/* Grid Export */}
              <div className="bg-muted/30 rounded-lg p-3 text-center">
                <ArrowUpFromLine className="h-4 w-4 mx-auto mb-1 text-success" />
                <div className="text-lg font-bold text-success">{formatNumber(system.gridExportKwh || 0, 1)}</div>
                <div className="text-xs text-muted-foreground">Grid Out</div>
              </div>
              {/* Battery Charge */}
              <div className="bg-muted/30 rounded-lg p-3 text-center">
                <Battery className="h-4 w-4 mx-auto mb-1 text-success" />
                <div className="text-lg font-bold">{formatNumber(system.batteryChargeKwh || 0, 1)}</div>
                <div className="text-xs text-muted-foreground">Batt Chg</div>
              </div>
              {/* Battery Discharge */}
              <div className="bg-muted/30 rounded-lg p-3 text-center">
                <Battery className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                <div className="text-lg font-bold">{formatNumber(system.batteryDischargeKwh || 0, 1)}</div>
                <div className="text-xs text-muted-foreground">Batt Dis</div>
              </div>
              {/* Generator */}
              <div className="bg-muted/30 rounded-lg p-3 text-center">
                <Fuel className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                <div className="text-lg font-bold">{formatNumber(system.generatorKwh || 0, 1)}</div>
                <div className="text-xs text-muted-foreground">Gen kWh</div>
              </div>
              {/* CO2 Reduced */}
              <div className="bg-muted/30 rounded-lg p-3 text-center">
                <Leaf className="h-4 w-4 mx-auto mb-1 text-success" />
                <div className="text-lg font-bold text-success">
                  {formatNumber(
                    (((system.dailyGeneration ?? system.todayGeneration ?? 0) * 7) / ENERGY_CONSTANTS.DIESEL_KWH_PER_GALLON) *
                    ENERGY_CONSTANTS.CO2_KG_PER_DIESEL_GALLON,
                    1
                  )}
                </div>
                <div className="text-xs text-muted-foreground">CO₂ kg</div>
              </div>
            </div>
          </div>
        )}

        {/* Financial Summary - Always show (uses generation data + effective rate) */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="h-4 w-4 text-success" />
            <span className="text-sm font-medium">Financial Summary (7 Day Est.)</span>
            {rate && (
              <span className="text-xs text-muted-foreground ml-auto">
                {rate.rateSource === 'fallback' ? 'Using default rates' : `Rate: ${rate.rateName}`}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Grid Sell Revenue */}
            <div className="bg-success/10 rounded-lg p-3 text-center">
              <ArrowUpFromLine className="h-4 w-4 mx-auto mb-1 text-success" />
              <div className="text-lg font-bold text-success">
                ${formatNumber((system.gridExportKwh || 0) * gridSellRate, 2)}
              </div>
              <div className="text-xs text-muted-foreground">Grid Sell</div>
            </div>
            {/* Grid Buy Cost */}
            <div className="bg-muted/30 rounded-lg p-3 text-center">
              <ArrowDownToLine className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <div className="text-lg font-bold">
                ${formatNumber((system.gridImportKwh || 0) * gridImportRate, 2)}
              </div>
              <div className="text-xs text-muted-foreground">Grid Cost</div>
            </div>
            {/* Solar Savings (avoided grid cost) */}
            <div className="bg-success/10 rounded-lg p-3 text-center">
              <Sun className="h-4 w-4 mx-auto mb-1 text-success" />
              <div className="text-lg font-bold text-success">
                ${formatNumber(
                  ((system.dailyGeneration ?? system.todayGeneration ?? 0) * 7) * gridImportRate,
                  2
                )}
              </div>
              <div className="text-xs text-muted-foreground">Solar Savings</div>
            </div>
            {/* Net Value */}
            <div className="bg-success/20 rounded-lg p-3 text-center">
              <DollarSign className="h-4 w-4 mx-auto mb-1 text-success" />
              <div className="text-lg font-bold text-success">
                ${formatNumber(
                  // Grid sell revenue + solar savings - grid buy cost
                  ((system.gridExportKwh || 0) * gridSellRate) +
                  (((system.dailyGeneration ?? system.todayGeneration ?? 0) * 7) * gridImportRate) -
                  ((system.gridImportKwh || 0) * gridImportRate),
                  2
                )}
              </div>
              <div className="text-xs text-muted-foreground">Net Value</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
